/**
 * Gerenciador de Sessão para Múltiplas Abas
 * 
 * Este utilitário oferece funcionalidades para gerenciar a sessão do usuário
 * através de múltiplas abas do navegador de forma inteligente e estável.
 */

interface TabInfo {
  id: string;
  timestamp: number;
  url: string;
  lastActivity: number;
}

interface SessionState {
  authenticated: boolean;
  lastActivity: number;
  activeTabsCount: number;
}

export class SessionManager {
  private static instance: SessionManager;
  private tabId: string;
  private activityInterval: NodeJS.Timeout | null = null;
  
  constructor() {
    this.tabId = this.generateTabId();
    this.setupActivityTracking();
    
    // Limpar abas inativas periodicamente
    this.cleanupInactiveTabs();
  }
  
  public static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }
  
  /**
   * Gerar ID único para a aba
   */
  private generateTabId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `tab_${timestamp}_${random}`;
  }
  
  /**
   * Configurar rastreamento de atividade da aba
   */
  private setupActivityTracking(): void {
    // Registrar atividade inicial
    this.updateActivity();
    
    // Atualizar atividade periodicamente
    this.activityInterval = setInterval(() => {
      this.updateActivity();
    }, 30000); // A cada 30 segundos
    
    // Eventos que indicam atividade do usuário
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    activityEvents.forEach(event => {
      document.addEventListener(event, this.throttledUpdateActivity.bind(this), true);
    });
    
    // Limpar ao fechar a aba
    window.addEventListener('beforeunload', () => {
      this.cleanup();
    });
    
    // Visibilidade da aba
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.updateActivity();
      }
    });
  }
  
  /**
   * Atualizar atividade da aba (throttled)
   */
  private throttledUpdateActivity = this.throttle(() => {
    this.updateActivity();
  }, 10000); // Máximo uma vez a cada 10 segundos
  
  /**
   * Atualizar atividade
   */
  private updateActivity(): void {
    try {
      const activeTabs = this.getActiveTabs();
      const existingTabIndex = activeTabs.findIndex(tab => tab.id === this.tabId);
      const now = Date.now();
      
      const tabInfo: TabInfo = {
        id: this.tabId,
        timestamp: now,
        url: window.location.href,
        lastActivity: now
      };
      
      if (existingTabIndex >= 0) {
        activeTabs[existingTabIndex] = tabInfo;
      } else {
        activeTabs.push(tabInfo);
      }
      
      // Limitar a 20 abas máximo (proteção)
      const recentTabs = activeTabs
        .filter(tab => now - tab.lastActivity < 600000) // Últimos 10 minutos
        .sort((a, b) => b.lastActivity - a.lastActivity)
        .slice(0, 20);
      
      localStorage.setItem('session_active_tabs', JSON.stringify(recentTabs));
      
      // Atualizar estado geral da sessão
      this.updateSessionState();
      
    } catch (e) {
      console.warn('[SESSION] Erro ao atualizar atividade da aba:', e);
    }
  }
  
  /**
   * Obter abas ativas
   */
  private getActiveTabs(): TabInfo[] {
    try {
      return JSON.parse(localStorage.getItem('session_active_tabs') || '[]');
    } catch (e) {
      return [];
    }
  }
  
  /**
   * Atualizar estado geral da sessão
   */
  private updateSessionState(): void {
    try {
      const activeTabs = this.getActiveTabs();
      const now = Date.now();
      
      // Considerar abas ativas apenas se tiveram atividade nos últimos 5 minutos
      const recentlyActiveTabs = activeTabs.filter(tab => 
        now - tab.lastActivity < 300000
      );
      
      const sessionState: SessionState = {
        authenticated: !!sessionStorage.getItem('token'),
        lastActivity: Math.max(...recentlyActiveTabs.map(tab => tab.lastActivity), now),
        activeTabsCount: recentlyActiveTabs.length
      };
      
      localStorage.setItem('session_state', JSON.stringify(sessionState));
      
    } catch (e) {
      console.warn('[SESSION] Erro ao atualizar estado da sessão:', e);
    }
  }
  
  /**
   * Verificar se a sessão está ativa em outras abas
   */
  public isSessionActiveInOtherTabs(): boolean {
    try {
      const activeTabs = this.getActiveTabs();
      const now = Date.now();
      
      // Verificar se há outras abas ativas nos últimos 2 minutos
      const otherActiveTabs = activeTabs.filter(tab => 
        tab.id !== this.tabId && 
        now - tab.lastActivity < 120000 // 2 minutos
      );
      
      return otherActiveTabs.length > 0;
    } catch (e) {
      return false;
    }
  }
  
  /**
   * Obter informações da sessão
   */
  public getSessionInfo(): SessionState | null {
    try {
      const sessionState = localStorage.getItem('session_state');
      return sessionState ? JSON.parse(sessionState) : null;
    } catch (e) {
      return null;
    }
  }
  
  /**
   * Limpeza ao fechar aba
   */
  private cleanup(): void {
    try {
      // Remover esta aba da lista de ativas
      const activeTabs = this.getActiveTabs();
      const filteredTabs = activeTabs.filter(tab => tab.id !== this.tabId);
      localStorage.setItem('session_active_tabs', JSON.stringify(filteredTabs));
      
      // Limpar interval
      if (this.activityInterval) {
        clearInterval(this.activityInterval);
        this.activityInterval = null;
      }
      
      console.log('[SESSION] Cleanup realizado para aba:', this.tabId);
    } catch (e) {
      console.warn('[SESSION] Erro durante cleanup:', e);
    }
  }
  
  /**
   * Limpar abas inativas periodicamente
   */
  private cleanupInactiveTabs(): void {
    setInterval(() => {
      try {
        const activeTabs = this.getActiveTabs();
        const now = Date.now();
        
        // Remover abas inativas há mais de 10 minutos
        const validTabs = activeTabs.filter(tab => 
          now - tab.lastActivity < 600000
        );
        
        if (validTabs.length !== activeTabs.length) {
          localStorage.setItem('session_active_tabs', JSON.stringify(validTabs));
          console.log('[SESSION] Limpeza automática:', {
            abasAntes: activeTabs.length,
            abasDepois: validTabs.length,
            removidas: activeTabs.length - validTabs.length
          });
        }
      } catch (e) {
        console.warn('[SESSION] Erro durante limpeza automática:', e);
      }
    }, 120000); // A cada 2 minutos
  }
  
  /**
   * Função throttle para limitar chamadas
   */
  private throttle(func: Function, delay: number): Function {
    let timeoutId: NodeJS.Timeout | null = null;
    let lastExecTime = 0;
    
    return (...args: any[]) => {
      const currentTime = Date.now();
      
      if (currentTime - lastExecTime > delay) {
        func.apply(this, args);
        lastExecTime = currentTime;
      } else {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          func.apply(this, args);
          lastExecTime = Date.now();
        }, delay - (currentTime - lastExecTime));
      }
    };
  }
  
  /**
   * Obter ID da aba atual
   */
  public getTabId(): string {
    return this.tabId;
  }
  
  /**
   * Verificar se esta aba está ativa
   */
  public isCurrentTabActive(): boolean {
    const activeTabs = this.getActiveTabs();
    const now = Date.now();
    
    const currentTab = activeTabs.find(tab => tab.id === this.tabId);
    return currentTab ? (now - currentTab.lastActivity < 300000) : false;
  }
}

// Exportar instância singleton
export const sessionManager = SessionManager.getInstance();

export default sessionManager;
